import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { isFeatureEnabled } from '../../../config/featureFlags';
import { isValidEmailForSending } from '../../../utils/validation';
import { CAMPAIGN_DAYS_OF_WEEK, DEFAULT_CAMPAIGN_CONFIG } from '../../../constants/scheduling';

// Shared components
import { AssistantMessage, UserMessage } from '../shared';

// Step components
import {
  LeadSelectionStep,
  TemplateSelectionStep,
  ValidationStep,
  SettingsStep,
  SendingStep,
  CompleteStep,
} from './steps';

// Hooks
import { useCampaignData, useTemplateValidation, useCampaignMessages } from './hooks';

// Modals
import { CampaignLaunchModal } from '../CampaignLaunchModal';
import { TemplateEditorModal } from '../TemplateEditorModal';

// Types
import type {
  CampaignCreationFlowProps,
  CampaignCreationState,
  CampaignCompletionSummary,
  CampaignFlowMessage,
  FlowStep,
  EmailTemplate,
} from './types';

// Maximum emails that can be sent per direct send to avoid rate limits
const MAX_EMAILS_PER_SEND = 20;

/**
 * Campaign Creation Flow Component
 *
 * Multi-step wizard for creating email campaigns with chat-like interface.
 * Steps: lead_selection → template → validation → settings → review
 */
export const CampaignCreationFlow: React.FC<CampaignCreationFlowProps> = ({
  tableId,
  tableName,
  columns,
  rows,
  totalLeadCount,
  filteredLeadCount,
  selectedLeadCount,
  selectedLeadIds,
  hasActiveFilters,
  onComplete,
  onEnterRowSelectionMode,
  onExitRowSelectionMode,
  onSelectAllRows,
  onSelectFirstNRows,
  onEnrichData,
  onAddMessages,
}) => {
  // Step state
  const [step, setStep] = useState<FlowStep>('lead_selection');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Custom hooks
  const {
    templates,
    gmailConnections,
    isLoading: dataLoading,
    setTemplates,
    getDefaultEmailAccount,
  } = useCampaignData();

  const { validateTemplate, getMissingVars } = useTemplateValidation(columns);

  const {
    messages,
    showOptions,
    isTyping,
    addAiMessage,
    addUserMessage,
    onAiMessageComplete,
    chatContainerRef,
  } = useCampaignMessages(tableName);

  // Campaign state - for direct send, limit initial lead count to MAX_EMAILS_PER_SEND
  const isDirectSendMode = !isFeatureEnabled('campaignScheduling');
  const [campaignState, setCampaignState] = useState<CampaignCreationState>(() => ({
    selectionMode: 'all',
    leadCount: isDirectSendMode ? Math.min(totalLeadCount, MAX_EMAILS_PER_SEND) : totalLeadCount,
    variableStatus: [],
    hasValidationIssues: false,
    timezone: 'America/New_York',
    startHour: 9,
    endHour: 17,
    daysOfWeek: [1, 2, 3, 4, 5],
    dailyLimit: 50,
  }));

  // UI state
  const [customCountInput, setCustomCountInput] = useState<string>(
    isDirectSendMode ? String(MAX_EMAILS_PER_SEND) : '25'
  );
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [hoveredTemplateId, setHoveredTemplateId] = useState<string | null>(null);
  const [showLaunchModal, setShowLaunchModal] = useState(false);

  // Ref to track current template selection (for race condition prevention)
  const currentTemplateIdRef = useRef<string | null>(null);

  // Set default email account when connections load
  useEffect(() => {
    const defaultAccount = getDefaultEmailAccount();
    if (defaultAccount && !campaignState.emailAccountId) {
      setCampaignState((prev) => ({
        ...prev,
        emailAccountId: defaultAccount.id,
        emailAccount: defaultAccount,
      }));
    }
  }, [gmailConnections, getDefaultEmailAccount, campaignState.emailAccountId]);

  // Sync lead count when source counts change (keeps state fresh for all modes)
  // For direct send, enforce MAX_EMAILS_PER_SEND limit
  useEffect(() => {
    setCampaignState((prev) => {
      const isDirectSend = !isFeatureEnabled('campaignScheduling');
      const maxAllowed = isDirectSend ? MAX_EMAILS_PER_SEND : Infinity;
      let newCount = prev.leadCount;

      switch (prev.selectionMode) {
        case 'all':
          newCount = Math.min(totalLeadCount, maxAllowed);
          break;
        case 'filtered':
          newCount = Math.min(filteredLeadCount ?? totalLeadCount, maxAllowed);
          break;
        case 'selected':
          newCount = Math.min(selectedLeadCount ?? 0, maxAllowed);
          break;
        case 'custom':
          // Clamp custom count to not exceed total or max allowed
          newCount = Math.min(prev.customCount ?? prev.leadCount, totalLeadCount, maxAllowed);
          break;
      }

      if (newCount === prev.leadCount) return prev;
      return { ...prev, leadCount: newCount };
    });
  }, [totalLeadCount, filteredLeadCount, selectedLeadCount, campaignState.selectionMode]);

  // Get user selection text for messages
  const getSelectionText = useCallback(() => {
    const count = campaignState.leadCount;
    switch (campaignState.selectionMode) {
      case 'all':
        return `All ${count.toLocaleString()} leads`;
      case 'filtered':
        return `${count.toLocaleString()} filtered leads`;
      case 'selected':
        return `${count.toLocaleString()} selected leads`;
      case 'custom':
        return `First ${count.toLocaleString()} leads`;
      default:
        return '';
    }
  }, [campaignState.leadCount, campaignState.selectionMode]);

  // Handle lead selection mode change
  // For direct send (non-campaign), limit to MAX_EMAILS_PER_SEND to avoid rate limits
  const handleLeadSelection = useCallback(
    (mode: CampaignCreationState['selectionMode']) => {
      const isDirectSend = !isFeatureEnabled('campaignScheduling');
      const maxAllowed = isDirectSend ? MAX_EMAILS_PER_SEND : totalLeadCount;

      let count = Math.min(totalLeadCount, maxAllowed);
      if (mode === 'filtered' && filteredLeadCount !== undefined) {
        count = Math.min(filteredLeadCount, maxAllowed);
      } else if (mode === 'selected') {
        count = Math.min(selectedLeadCount || 0, maxAllowed);
      } else if (mode === 'custom') {
        count = Math.min(parseInt(customCountInput) || 20, maxAllowed);
      }

      // Exit previous selection modes
      if (mode !== 'selected') {
        onExitRowSelectionMode?.();
      }

      // Apply the selection
      if (mode === 'all') {
        if (isDirectSend) {
          onSelectFirstNRows?.(Math.min(totalLeadCount, maxAllowed));
        } else {
          onSelectAllRows?.();
        }
      } else if (mode === 'custom') {
        const validCount = Math.min(parseInt(customCountInput) || 20, maxAllowed, totalLeadCount);
        onSelectFirstNRows?.(validCount);
      } else if (mode === 'selected') {
        onEnterRowSelectionMode?.();
      }

      setCampaignState((prev) => ({
        ...prev,
        selectionMode: mode,
        leadCount: count,
        customCount: mode === 'custom' ? count : undefined,
      }));
    },
    [
      totalLeadCount,
      filteredLeadCount,
      selectedLeadCount,
      customCountInput,
      onExitRowSelectionMode,
      onSelectAllRows,
      onSelectFirstNRows,
      onEnterRowSelectionMode,
    ]
  );

  // Handle custom count input change
  const handleCustomCountChange = useCallback(
    (value: string) => {
      setCustomCountInput(value);
      const isDirectSend = !isFeatureEnabled('campaignScheduling');
      const maxAllowed = isDirectSend ? MAX_EMAILS_PER_SEND : totalLeadCount;
      const count = parseInt(value) || 0;
      const validCount = Math.max(0, Math.min(count, totalLeadCount, maxAllowed));
      setCampaignState((prev) => ({
        ...prev,
        selectionMode: 'custom',
        leadCount: validCount,
        customCount: validCount,
      }));
      onSelectFirstNRows?.(validCount);
    },
    [totalLeadCount, onSelectFirstNRows]
  );

  // Proceed from lead selection
  const handleProceedFromLeadSelection = useCallback(() => {
    if (campaignState.leadCount === 0) {
      setError('Please select at least one lead');
      return;
    }
    if (
      campaignState.selectionMode === 'selected' &&
      (!selectedLeadCount || selectedLeadCount === 0)
    ) {
      setError('Please select at least one row from the table');
      return;
    }
    setError(null);
    onExitRowSelectionMode?.();

    addUserMessage(getSelectionText(), 'lead_selection');

    setTimeout(() => {
      addAiMessage(
        `${campaignState.leadCount.toLocaleString()} leads ready. Which template would you like to use?`,
        'template'
      );
      setStep('template');
    }, 400);
  }, [
    campaignState.leadCount,
    campaignState.selectionMode,
    selectedLeadCount,
    onExitRowSelectionMode,
    addUserMessage,
    getSelectionText,
    addAiMessage,
  ]);

  // Handle template selection
  const handleSelectTemplate = useCallback(
    async (template: EmailTemplate) => {
      // Track this template as the current selection
      currentTemplateIdRef.current = template.id;

      addUserMessage(template.subject, 'template');

      setCampaignState((prev) => ({
        ...prev,
        templateId: template.id,
        template,
      }));

      setIsLoading(true);
      const { hasIssues, statuses } = await validateTemplate(
        template,
        campaignState.leadCount
      );
      setIsLoading(false);

      // Guard against stale async response - only apply if this is still the selected template
      if (currentTemplateIdRef.current !== template.id) {
        return;
      }

      // Update campaign state with validation results
      setCampaignState((prev) => ({
        ...prev,
        variableStatus: statuses,
        hasValidationIssues: hasIssues,
      }));

      setTimeout(() => {
        // Double-check template hasn't changed before advancing step
        if (currentTemplateIdRef.current !== template.id) {
          return;
        }

        if (hasIssues) {
          const missingCount = statuses.filter((s) => s.status !== 'ok').length;
          addAiMessage(
            `I found ${missingCount} variable${missingCount > 1 ? 's' : ''} in this template that ${missingCount === 1 ? "doesn't" : "don't"} have matching data in your dataset. How would you like to proceed?`,
            'validation'
          );
          setStep('validation');
        } else if (isFeatureEnabled('campaignScheduling')) {
          // Campaign scheduling enabled - show settings step
          addAiMessage(
            "Almost there! Configure when you'd like to send these emails. I'll spread them out naturally throughout the day for better deliverability.",
            'settings'
          );
          setStep('settings');
        } else {
          // Direct send mode - skip settings, open launch modal directly
          addAiMessage(
            `Ready to send ${campaignState.leadCount.toLocaleString()} emails. Review your campaign details.`,
            'review'
          );
          setStep('review');
          // Open modal after a brief delay to let the message render
          setTimeout(() => {
            setShowLaunchModal(true);
          }, 300);
        }
      }, 400);
    },
    [addUserMessage, validateTemplate, campaignState.leadCount, addAiMessage]
  );

  // Handle choosing another template
  const handleChooseAnotherTemplate = useCallback(() => {
    addUserMessage('Choose another template', 'validation');
    setHoveredTemplateId(null);

    setTimeout(() => {
      addAiMessage('No problem! Which template would you like to use instead?', 'template');
      setStep('template');
    }, 400);
  }, [addUserMessage, addAiMessage]);

  // Proceed from validation
  const handleProceedFromValidation = useCallback(() => {
    addUserMessage('Continue anyway', 'validation');

    setTimeout(() => {
      if (isFeatureEnabled('campaignScheduling')) {
        // Campaign scheduling enabled - show settings step
        addAiMessage(
          "Almost there! Configure when you'd like to send these emails. I'll spread them out naturally throughout the day for better deliverability.",
          'settings'
        );
        setStep('settings');
      } else {
        // Direct send mode - skip settings, open launch modal directly
        addAiMessage(
          `Ready to send ${campaignState.leadCount.toLocaleString()} emails. Review your campaign details.`,
          'review'
        );
        setStep('review');
        // Open modal after a brief delay to let the message render
        setTimeout(() => {
          setShowLaunchModal(true);
        }, 300);
      }
    }, 400);
  }, [addUserMessage, addAiMessage, campaignState.leadCount]);

  // Open launch modal
  const handleOpenLaunchModal = useCallback(() => {
    if (!campaignState.emailAccountId) {
      setError('Please select an email account');
      return;
    }
    setError(null);

    addUserMessage('Review & Launch', 'settings');
    setStep('review');

    setTimeout(() => {
      setShowLaunchModal(true);
    }, 300);
  }, [campaignState.emailAccountId, addUserMessage]);

  // Launch campaign
  const handleLaunchCampaign = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.createCampaign({
        name: `${tableName} Campaign`,
        dataset_ids: [tableId],
        credential_id: campaignState.emailAccountId,
        // Include template - either by ID or inline subject/body
        email_template_id: campaignState.templateId,
        email_subject: campaignState.template?.subject,
        email_body: campaignState.template?.body,
        timezone: campaignState.timezone,
        send_window_start: `${String(campaignState.startHour).padStart(2, '0')}:00`,
        send_window_end: `${String(campaignState.endHour).padStart(2, '0')}:00`,
        send_days: campaignState.daysOfWeek
          .map((d) => CAMPAIGN_DAYS_OF_WEEK.find((day) => day.value === d)?.label as string)
          .filter(Boolean) as string[],
        daily_limit: campaignState.dailyLimit,
      });

      const campaignId = response.id;

      // Enroll recipients based on selection mode
      try {
        if (campaignState.selectionMode === 'selected' && selectedLeadIds && selectedLeadIds.length > 0) {
          // Enroll specific selected leads
          await apiClient.enrollRecipients(campaignId, selectedLeadIds);
        } else {
          // Enroll from dataset (all or custom count)
          await apiClient.enrollFromDataset(campaignId, tableId, {
            limit: campaignState.selectionMode === 'custom' ? campaignState.leadCount : undefined,
          });
        }
      } catch (enrollErr: any) {
        console.error('Failed to enroll recipients:', enrollErr);
        // Campaign was created, so we still complete but log the enrollment error
      }

      setShowLaunchModal(false);
      onComplete(campaignId);
    } catch (err: any) {
      console.error('Failed to create campaign:', err);
      setError(err?.message || 'Failed to create campaign');
    } finally {
      setIsLoading(false);
    }
  }, [
    tableName,
    tableId,
    campaignState,
    selectedLeadIds,
    onComplete,
  ]);

  // Direct send state (when campaign scheduling is disabled)
  const [sendProgress, setSendProgress] = useState(0);
  const [sendResults, setSendResults] = useState<{ success: number; failed: number; lastError?: string }>({ success: 0, failed: 0 });
  const [emailColumn, setEmailColumn] = useState<string>(() => {
    // Auto-select first email column if available
    const emailCol = columns.find(col =>
      col.key.toLowerCase().includes('email') ||
      col.name.toLowerCase().includes('email')
    );
    return emailCol?.key || '';
  });

  // Track created campaign for navigation and summary after direct send
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);
  const [campaignSummary, setCampaignSummary] = useState<CampaignCompletionSummary | null>(null);

  // Handle direct email sending (when campaign scheduling is disabled)
  // Always creates a campaign for tracking, enrolls recipients, sends emails, updates statuses
  const handleDirectSend = useCallback(async () => {
    if (!campaignState.emailAccountId || !campaignState.template || !emailColumn) {
      setError('Please select an email account and email column');
      return;
    }

    if (!rows || rows.length === 0) {
      setError('No lead data available');
      return;
    }

    setIsLoading(true);
    setError(null);
    setShowLaunchModal(false);  // Close modal before sending
    setStep('sending');
    setSendProgress(0);
    setSendResults({ success: 0, failed: 0, lastError: undefined });

    addUserMessage('Send Now', 'review');

    try {
      // Use passed rows instead of fetching from API
      let leads = [...rows];

      // Filter based on selection mode
      if (campaignState.selectionMode === 'selected' && selectedLeadIds?.length) {
        leads = leads.filter((lead) => selectedLeadIds.includes(lead.id));
      } else if (campaignState.selectionMode === 'custom' || campaignState.selectionMode === 'filtered') {
        leads = leads.slice(0, campaignState.leadCount);
      }

      // Access email from the selected column in lead.data
      const selectedCol = columns.find(c => c.key === emailColumn);
      const getLeadEmail = (lead: any) => {
        const data = lead.data || {};
        if (data[emailColumn]) return data[emailColumn];
        if (selectedCol?.name && data[selectedCol.name]) return data[selectedCol.name];
        const dataKey = Object.keys(data).find(k =>
          k.toLowerCase() === emailColumn.toLowerCase() ||
          (selectedCol?.name && k.toLowerCase() === selectedCol.name.toLowerCase())
        );
        if (dataKey) return data[dataKey];
        return lead[emailColumn] || (selectedCol?.name && lead[selectedCol.name]);
      };

      const leadsWithEmail = leads.filter((lead: any) => {
        const email = getLeadEmail(lead);
        return isValidEmailForSending(email);
      });

      if (leadsWithEmail.length === 0) {
        setError('No leads with valid email addresses found');
        setStep('review');
        setIsLoading(false);
        return;
      }

      const leadsToSend = leadsWithEmail.slice(0, MAX_EMAILS_PER_SEND);
      const wasLimited = leadsWithEmail.length > MAX_EMAILS_PER_SEND;

      // Step 1: Create campaign for tracking (required)
      // Uses default config since scheduling is disabled - these are placeholder values for the API
      const campaignName = `${tableName} - Quick Send`;
      let campaignId: string;
      try {
        const campaignResponse = await apiClient.createCampaign({
          name: campaignName,
          dataset_ids: [tableId],
          credential_id: campaignState.emailAccountId,
          email_subject: campaignState.template.subject,
          email_body: campaignState.template.body,
          timezone: DEFAULT_CAMPAIGN_CONFIG.timezone,
          send_window_start: DEFAULT_CAMPAIGN_CONFIG.sendWindowStart,
          send_window_end: DEFAULT_CAMPAIGN_CONFIG.sendWindowEnd,
          send_days: [...DEFAULT_CAMPAIGN_CONFIG.sendDays],
          daily_limit: DEFAULT_CAMPAIGN_CONFIG.dailyLimit,
        });
        campaignId = campaignResponse.id;
        setCreatedCampaignId(campaignId);
      } catch (campaignErr: any) {
        console.error('Failed to create campaign:', campaignErr);
        setError('Failed to create campaign for tracking. Please try again.');
        setStep('review');
        setIsLoading(false);
        return;
      }

      // Step 2: Enroll recipients and get their campaign recipient IDs
      const leadIdsToEnroll = leadsToSend.map((lead: any) => lead.id);
      const recipientMap: Map<string, string> = new Map(); // lead_row_id -> recipient_id

      // Get the column name for the selected email column (backend matches by name)
      const selectedEmailCol = columns.find(c => c.key === emailColumn);
      const emailColumnName = selectedEmailCol?.name || emailColumn;

      try {
        await apiClient.enrollRecipients(campaignId, leadIdsToEnroll, { emailColumn: emailColumnName });

        // Fetch enrolled recipients to get their IDs for status updates
        const recipientsResponse = await apiClient.listCampaignRecipients(campaignId, { limit: 100 });
        if (recipientsResponse?.recipients) {
          for (const recipient of recipientsResponse.recipients) {
            if (recipient.lead_row_id) {
              recipientMap.set(recipient.lead_row_id, recipient.id);
            }
          }
        }
      } catch (enrollErr: any) {
        console.error('Failed to enroll recipients:', enrollErr);
        // Continue - emails will still be sent, just without recipient tracking
      }

      // Step 3: Send emails and update recipient statuses
      let successCount = 0;
      let failCount = 0;
      const totalCount = leadsToSend.length;

      const replaceVariables = (text: string, lead: any) => {
        const data = lead.data || lead;
        const firstName = data.first_name || data.name?.split(' ')[0] || '';
        const lastName = data.last_name || data.name?.split(' ').slice(1).join(' ') || '';
        const fullName = data.name || `${firstName} ${lastName}`.trim();
        const company = data.company || data.company_name || '';

        return text
          .replace(/\{\{first_name\}\}/gi, firstName)
          .replace(/\{\{last_name\}\}/gi, lastName)
          .replace(/\{\{name\}\}/gi, fullName)
          .replace(/\{\{company\}\}/gi, company)
          .replace(/\{\{email\}\}/gi, getLeadEmail(lead) || '');
      };

      let lastError: string | undefined;
      for (const lead of leadsToSend) {
        const personalizedSubject = replaceVariables(campaignState.template!.subject, lead);
        const personalizedBody = replaceVariables(campaignState.template!.body, lead);
        const recipientId = recipientMap.get(lead.id);
        let sendSuccess = false;

        try {
          const result = await apiClient.sendEmail({
            recipient: getLeadEmail(lead),
            subject: personalizedSubject,
            body: personalizedBody,
            gmail_connection_id: campaignState.emailAccountId!,
          });

          if (result && result.success === true) {
            successCount++;
            sendSuccess = true;
          } else {
            failCount++;
            lastError = result?.error || 'Email send failed';
          }
        } catch (err: any) {
          failCount++;
          lastError = err?.message || err?.response?.data?.detail || 'Network error';
        }

        // Update recipient status in campaign
        if (recipientId) {
          try {
            await apiClient.updateRecipientStatus(
              campaignId,
              recipientId,
              sendSuccess ? 'completed' : 'bounced'
            );
          } catch (statusErr) {
            console.error('Failed to update recipient status:', statusErr);
          }
        }

        const processed = successCount + failCount;
        setSendProgress(Math.round((processed / totalCount) * 100));
        setSendResults({ success: successCount, failed: failCount, lastError });
      }

      // Step 4: Mark campaign as completed
      try {
        await apiClient.updateCampaign(campaignId, { status: 'completed' });
      } catch (updateErr) {
        console.error('Failed to update campaign status:', updateErr);
      }

      if (wasLimited) {
        console.log(`Limited send to ${MAX_EMAILS_PER_SEND} emails. ${leadsWithEmail.length - MAX_EMAILS_PER_SEND} leads skipped.`);
      }

      // Create completion summary for history
      const summary: CampaignCompletionSummary = {
        campaignId,
        campaignName,
        templateName: campaignState.template.name || 'Custom Template',
        templateSubject: campaignState.template.subject,
        recipientCount: totalCount,
        sentCount: successCount,
        failedCount: failCount,
        senderEmail: campaignState.emailAccount?.gmail_email || '',
        completedAt: new Date(),
      };
      setCampaignSummary(summary);

      // Persist campaign summary to chat history
      if (onAddMessages) {
        const now = new Date();
        const historyMessages: CampaignFlowMessage[] = [
          {
            id: `campaign-user-${now.getTime()}`,
            role: 'user',
            content: `Send email campaign to ${totalCount} recipients`,
            timestamp: now,
          },
          {
            id: `campaign-assistant-${now.getTime()}`,
            role: 'assistant',
            content: `**Campaign "${campaignName}" completed**\n\n• **Template:** ${summary.templateSubject}\n• **Sent:** ${successCount} emails${failCount > 0 ? `\n• **Failed:** ${failCount}` : ''}\n• **From:** ${summary.senderEmail}\n\n[View Campaign →](/campaigns/${campaignId})`,
            timestamp: now,
          },
        ];
        onAddMessages(historyMessages);
      }

      setSendProgress(100);
      setSendResults({ success: successCount, failed: failCount, lastError });
      setStep('complete');

      // Trigger notification email (fire and forget - don't block UI)
      if (successCount > 0) {
        apiClient.notifyEmailsSent({
          dataset_id: tableId,
          emails_sent: successCount,
          emails_failed: failCount,
        }).catch(() => {
          // Silent failure - notification is non-critical
        });
      }

      setTimeout(() => {
        let message = `Done! ${successCount} email${successCount !== 1 ? 's' : ''} sent successfully${failCount > 0 ? `, ${failCount} failed` : ''}.`;
        if (wasLimited) {
          message += ` Note: Limited to ${MAX_EMAILS_PER_SEND} emails per send.`;
        }
        addAiMessage(message, 'complete');
      }, 300);

    } catch (err: any) {
      console.error('Failed to send emails:', err);
      setError(err?.message || 'Failed to send emails');
      setStep('review');
    } finally {
      setIsLoading(false);
    }
  }, [rows, campaignState, selectedLeadIds, emailColumn, addUserMessage, addAiMessage, columns, tableName, tableId, onAddMessages]);

  // Calculate estimated days
  const estimatedDays = Math.ceil(
    campaignState.leadCount /
      ((campaignState.dailyLimit * campaignState.daysOfWeek.length) / 7)
  );

  // Render loading state
  if (dataLoading && step === 'lead_selection' && templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin mb-4" />
        <p className="text-[14px] text-gray-500">Setting up campaign...</p>
      </div>
    );
  }

  const missingVars = getMissingVars();

  return (
    <div className="flex flex-col h-full w-full">
      {/* Chat Messages */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto space-y-4 p-4 pt-2">
        {/* Render all messages */}
        {messages.map((message, index) => {
          const isLatest = index === messages.length - 1;

          if (message.type === 'ai') {
            return (
              <AssistantMessage
                key={message.id}
                isAnimating={isLatest && message.isAnimating}
                onAnimationComplete={onAiMessageComplete}
                animationText={message.content}
              >
                {message.content}
              </AssistantMessage>
            );
          } else {
            return <UserMessage key={message.id}>{message.content}</UserMessage>;
          }
        })}

        {/* Current Step Options (shown after AI message animation completes) */}
        {showOptions && !isTyping && (
          <div className="pl-10">
            {step === 'lead_selection' && (
              <LeadSelectionStep
                totalLeadCount={totalLeadCount}
                filteredLeadCount={filteredLeadCount}
                selectedLeadCount={selectedLeadCount}
                hasActiveFilters={hasActiveFilters}
                selectionMode={campaignState.selectionMode}
                customCountInput={customCountInput}
                error={error}
                maxLeadCount={!isFeatureEnabled('campaignScheduling') ? MAX_EMAILS_PER_SEND : undefined}
                onSelectionChange={handleLeadSelection}
                onCustomCountChange={handleCustomCountChange}
                onProceed={handleProceedFromLeadSelection}
              />
            )}

            {step === 'template' && (
              <TemplateSelectionStep
                templates={templates}
                hoveredTemplateId={hoveredTemplateId}
                onHoverTemplate={setHoveredTemplateId}
                onSelectTemplate={handleSelectTemplate}
                onPreviewTemplate={(template) => {
                  setPreviewTemplate(template);
                  setShowTemplateModal(true);
                }}
                onCreateTemplate={() => {
                  setPreviewTemplate(null);
                  setShowTemplateModal(true);
                }}
              />
            )}

            {step === 'validation' && (
              <ValidationStep
                missingVars={missingVars}
                onEnrichData={onEnrichData}
                onChooseAnotherTemplate={handleChooseAnotherTemplate}
                onContinueAnyway={handleProceedFromValidation}
              />
            )}

            {step === 'settings' && (
              <SettingsStep
                campaignState={campaignState}
                gmailConnections={gmailConnections}
                estimatedDays={estimatedDays}
                error={error}
                onUpdateState={(updates) =>
                  setCampaignState((prev) => ({ ...prev, ...updates }))
                }
                onLaunch={handleOpenLaunchModal}
              />
            )}

            {/* Direct Send UI (when campaign scheduling is disabled) - show button to open modal */}
            {step === 'review' && !isFeatureEnabled('campaignScheduling') && !showLaunchModal && (
              <div className="mt-3">
                <button
                  onClick={() => setShowLaunchModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Review & Send {campaignState.leadCount.toLocaleString()} Emails
                </button>
              </div>
            )}

            {/* Sending progress */}
            {step === 'sending' && (
              <SendingStep
                leadCount={campaignState.leadCount}
                sendProgress={sendProgress}
                sendResults={sendResults}
              />
            )}

            {/* Completion */}
            {step === 'complete' && (
              <CompleteStep
                sendResults={sendResults}
                campaignId={createdCampaignId}
                onDone={() => onComplete(createdCampaignId || 'direct-send', campaignSummary || undefined)}
              />
            )}
          </div>
        )}

        {/* Bottom spacer for scroll behavior */}
        <div className="flex-shrink-0 min-h-[200px]" />
      </div>

      {/* Launch Modal */}
      {showLaunchModal && (
        <CampaignLaunchModal
          isOpen={showLaunchModal}
          onClose={() => {
            setShowLaunchModal(false);
            // In direct send mode, go back to review step (settings doesn't exist)
            setStep(isDirectSendMode ? 'review' : 'settings');
          }}
          onLaunch={isDirectSendMode ? handleDirectSend : handleLaunchCampaign}
          isLoading={isLoading}
          campaignState={campaignState}
          tableName={tableName}
          tableId={tableId}
          selectedLeadIds={selectedLeadIds}
          onUpdateState={(updates) => setCampaignState((prev) => ({ ...prev, ...updates }))}
          onEditSettings={isDirectSendMode ? undefined : () => {
            setShowLaunchModal(false);
            setStep('settings');
          }}
          onEditTemplate={() => {
            setShowLaunchModal(false);
            setStep('template');
          }}
          // Direct send mode props
          columns={isDirectSendMode ? columns : undefined}
          emailColumn={isDirectSendMode ? emailColumn : undefined}
          onEmailColumnChange={isDirectSendMode ? setEmailColumn : undefined}
          gmailConnections={isDirectSendMode ? gmailConnections : undefined}
        />
      )}

      {/* Template Editor Modal */}
      {showTemplateModal && (
        <TemplateEditorModal
          isOpen={showTemplateModal}
          onClose={() => {
            setShowTemplateModal(false);
            setPreviewTemplate(null);
          }}
          templates={templates}
          initialTemplate={previewTemplate}
          columns={columns.map((c) => c.key || c.name)}
          onSave={(template) => {
            // Add new template to local list immediately
            setTemplates((prev) => {
              const exists = prev.some((t) => t.id === template.id);
              if (!exists) {
                return [template, ...prev];
              }
              return prev.map((t) => (t.id === template.id ? template : t));
            });
            setHoveredTemplateId(template.id);
            setShowTemplateModal(false);
            setPreviewTemplate(null);
          }}
        />
      )}
    </div>
  );
};

CampaignCreationFlow.displayName = 'CampaignCreationFlow';
